import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './WeighIn.css';
import './Button.css';
import Modal from './Modal';

const WeighIn = ({ isFullscreen, toggleFullscreen }) => {
    const [licenseNumber, setLicenseNumber] = useState('');
    const [sharkId, setSharkId] = useState('');
    const [angler, setAngler] = useState(null);
    const containerRef = useRef(null);
    const licenseInputRef = useRef(null);
    const sharkInputRef = useRef(null);
    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const licenseNumberInputRef = useRef(null);
    const businessInputRef = useRef(null);
    const registerCatchButtonRef = useRef(null);
    const [shark, setShark] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success', 'error', or 'warning'
    const [currentRound, setCurrentRound] = useState(1);
    const [results, setResults] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [newAngler, setNewAngler] = useState({
        first_name: '',
        last_name: '',
        license_number: '',
        calcutta: false,
        extra_bait: false,
        business: ''
    });

    useEffect(() => {
        fetchCurrentRound();
        // Automatically scroll to the top of the weigh-in component
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        // Focus the license input for immediate scanning
        if (licenseInputRef.current) {
            licenseInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (isModalOpen && modalContent === 'register') {
            setTimeout(() => {
                if (firstNameInputRef.current) {
                    firstNameInputRef.current.focus();
                }
            }, 100); // A small delay to ensure the modal is fully rendered
        }
    }, [isModalOpen, modalContent]);

    const fetchCurrentRound = async () => {
        try {
            const response = await api.get('/tournament/round');
            setCurrentRound(response.data.current_round);
        } catch (error) {
            setMessage('Error fetching current round.');
            setMessageType('error');
        }
    };

    const handleLicenseScan = async () => {
        try {
            const response = await api.get(`/anglers/by_license/${licenseNumber}`);
            const fetchedAngler = response.data;
            setAngler(fetchedAngler);

            const catchesResponse = await api.get(`/results/${fetchedAngler.id}`);
            const catches = catchesResponse.data.top_catches;

            if (catches.length >= currentRound) {
                setMessage(`Angler has already weighed in for round ${currentRound}.`);
                setMessageType('warning');
            } else {
                setMessage(`Angler ${fetchedAngler.first_name} ${fetchedAngler.last_name} found.`);
                setMessageType('success');
            }
            fetchAnglerResults(fetchedAngler.id);

        } catch (error) {
            setAngler(null);
            setModalContent('register');
            setIsModalOpen(true);
            setNewAngler({ ...newAngler, license_number: licenseNumber });
        }
    };

    const handleSharkScan = async () => {
        try {
            const response = await api.get(`/sharks/by_shark_id/${sharkId}?round=${currentRound}`);
            setShark(response.data);
            setMessage(`Shark with weight ${response.data.weight_lbs} lbs ${response.data.weight_oz} oz found.`);
            setMessageType('success');
        } catch (error) {
            setShark(null);
            setMessage('Shark not found.');
            setMessageType('error');
        }
    };

    const fetchAnglerResults = async (anglerId) => {
        try {
            const response = await api.get(`/results/${anglerId}`);
            setResults(response.data);
        } catch (error) {
            setResults(null);
        }
    };

    const handleRegisterCatch = async () => {
        if (!angler || !shark) {
            setMessage('Please scan both a license and a shark ID.');
            setMessageType('error');
            return;
        }

        if (currentRound === 3 && !angler.extra_bait) {
            setModalContent('extraBait');
            setIsModalOpen(true);
            return;
        }

        registerCatch();
    };

    const registerCatch = async () => {
        try {
            await api.post('/weigh-in/', {
                angler_id: angler.id,
                shark_id: shark.id,
                round: currentRound,
            });
            setMessage('Catch registered successfully!');
            setMessageType('success');
            setLicenseNumber('');
            setSharkId('');
            setAngler(null);
            setShark(null);
            setResults(null);
            if (licenseInputRef.current) {
                licenseInputRef.current.focus();
            }
        } catch (error) {
            setMessage(error.response.data.detail || 'Error registering catch.');
            setMessageType('error');
        }
    };

    const handleAddExtraBait = async () => {
        try {
            await api.put(`/anglers/${angler.id}`, { ...angler, extra_bait: true });
            setIsModalOpen(false);
            registerCatch();
        } catch (error) {
            setMessage('Failed to add extra bait.');
            setMessageType('error');
            setIsModalOpen(false);
        }
    };

    const handleNewAnglerChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewAngler({
            ...newAngler,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleRegisterNewAngler = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/anglers/', newAngler);
            setAngler(response.data);
            setMessage('Angler registered successfully.');
            setMessageType('success');
            setIsModalOpen(false);
        } catch (error) {
            setMessage('Error registering angler.');
            setMessageType('error');
        }
    };

    const handleKeyDown = (e, nextFieldRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextFieldRef && nextFieldRef.current) {
                nextFieldRef.current.focus();
            }
        }
    };
 
     return (
         <div ref={containerRef} className={`weigh-in-container ${isFullscreen ? 'is-fullscreen' : ''}`}>
            <div className="weigh-in-header">
                <h2>Weigh-In</h2>
                <button onClick={toggleFullscreen} className="btn btn-secondary fullscreen-toggle">
                    {isFullscreen ? 'Show Header/Footer' : 'Maximize Display'}
                </button>
            </div>
            <div className="round-management">
                <h3>Current Round: {currentRound}</h3>
            </div>
            <div className="weigh-in-main-content">
                <div className="weigh-in-left-panel">
                    <div className="weigh-in-form">
                        <div className="weigh-in-form-header">Scan Entry</div>
                        <div className="weigh-in-form-body">
                            <div className="scan-field">
                                <label htmlFor="license-input">License Number</label>
                                <input
                                    id="license-input"
                                    type="text"
                                    value={licenseNumber}
                                    onChange={(e) => setLicenseNumber(e.target.value)}
                                    onBlur={handleLicenseScan}
                                    placeholder="Scan or type..."
                                    ref={licenseInputRef}
                                    onKeyDown={(e) => handleKeyDown(e, sharkInputRef)}
                                />
                            </div>
                            <div className="scan-field">
                                <label htmlFor="shark-input">Shark ID</label>
                                <input
                                    id="shark-input"
                                    type="text"
                                    value={sharkId}
                                    onChange={(e) => setSharkId(e.target.value)}
                                    onBlur={handleSharkScan}
                                    placeholder="Scan or type..."
                                    ref={sharkInputRef}
                                    onKeyDown={(e) => handleKeyDown(e, registerCatchButtonRef)}
                                />
                            </div>
                            <button className="btn btn-primary" onClick={handleRegisterCatch} ref={registerCatchButtonRef}>Register Catch</button>
                        </div>
                    </div>
                    {message && <p className={`message ${messageType}`}>{message}</p>}
                </div>
                <div className="weigh-in-middle-panel">
                    {angler ? (
                        <div className="details-section">
                            <div className="details-header">Angler Details</div>
                            <div className="details-body">
                                <div className="detail-row">
                                    <span className="detail-label">Name</span>
                                    <span className="detail-value">{angler.first_name} {angler.last_name}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">License</span>
                                    <span className="detail-value">{angler.license_number}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Extra Bait</span>
                                    <span className={`detail-badge ${angler.extra_bait ? 'badge-yes' : 'badge-no'}`}>
                                        {angler.extra_bait ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Calcutta</span>
                                    <span className={`detail-badge ${angler.calcutta ? 'badge-yes' : 'badge-no'}`}>
                                        {angler.calcutta ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                {angler.business && (
                                    <div className="detail-row">
                                        <span className="detail-label">Business</span>
                                        <span className="detail-value">{angler.business}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="details-section empty">
                            <div className="details-header">Angler Details</div>
                            <div className="details-body">
                                <p className="placeholder">Scan a license to view angler details</p>
                            </div>
                        </div>
                    )}

                    {shark ? (
                        <div className="details-section">
                            <div className="details-header">Shark Details</div>
                            <div className="details-body">
                                <div className="detail-row">
                                    <span className="detail-label">Weight</span>
                                    <span className="detail-value detail-weight">{shark.weight_lbs} lbs {shark.weight_oz} oz</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Round</span>
                                    <span className="detail-value">{shark.round}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="details-section empty">
                            <div className="details-header">Shark Details</div>
                            <div className="details-body">
                                <p className="placeholder">Scan a shark ID to view weight</p>
                            </div>
                        </div>
                    )}
                </div>
                <div className="weigh-in-right-panel">
                    {results ? (
                        <div className="details-section">
                            <div className="details-header">Angler Results</div>
                            <div className="details-body">
                                <div className="detail-row total-row">
                                    <span className="detail-label">Total Weight</span>
                                    <span className="detail-value detail-weight">{results.total_weight_lbs} lbs {results.total_weight_oz} oz</span>
                                </div>
                                <p className="catches-heading">Top Catches</p>
                                <div className="catch-list">
                                    {results.top_catches.map((c) => (
                                        <div key={c.id} className="catch-row">
                                            <span className="catch-round">Round {c.round || '?'}</span>
                                            <span className="catch-weight">{c.shark.weight_lbs} lbs {c.shark.weight_oz} oz</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="details-section empty">
                            <div className="details-header">Angler Results</div>
                            <div className="details-body">
                                <p className="placeholder">Scan a license to view results</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                {modalContent === 'register' && (
                    <div className="modal-register">
                        <h4>Register New Angler</h4>
                        <p className="modal-sub">License not found — fill in details to register.</p>
                        <form onSubmit={handleRegisterNewAngler} className="modal-form">
                            <div className="modal-form-row">
                                <div className="modal-form-group">
                                    <label htmlFor="m_first_name">First Name</label>
                                    <input id="m_first_name" type="text" name="first_name" value={newAngler.first_name} onChange={handleNewAnglerChange} placeholder="First name" ref={firstNameInputRef} onKeyDown={(e) => handleKeyDown(e, lastNameInputRef)} />
                                </div>
                                <div className="modal-form-group">
                                    <label htmlFor="m_last_name">Last Name</label>
                                    <input id="m_last_name" type="text" name="last_name" value={newAngler.last_name} onChange={handleNewAnglerChange} placeholder="Last name" ref={lastNameInputRef} onKeyDown={(e) => handleKeyDown(e, licenseNumberInputRef)} />
                                </div>
                            </div>
                            <div className="modal-form-group">
                                <label htmlFor="m_license">License Number <span className="required">*</span></label>
                                <input id="m_license" type="text" name="license_number" value={newAngler.license_number} onChange={handleNewAnglerChange} placeholder="License number" required ref={licenseNumberInputRef} onKeyDown={(e) => handleKeyDown(e, businessInputRef)} />
                            </div>
                            <div className="modal-form-group">
                                <label htmlFor="m_business">Business / Vessel</label>
                                <input id="m_business" type="text" name="business" value={newAngler.business} onChange={handleNewAnglerChange} placeholder="Optional" ref={businessInputRef} />
                            </div>
                            <div className="modal-checkbox-group">
                                <label className="checkbox-label">
                                    <input type="checkbox" name="calcutta" checked={newAngler.calcutta} onChange={handleNewAnglerChange} />
                                    <span className="checkbox-track"><span className="checkbox-thumb" /></span>
                                    Calcutta
                                </label>
                                <label className="checkbox-label">
                                    <input type="checkbox" name="extra_bait" checked={newAngler.extra_bait} onChange={handleNewAnglerChange} />
                                    <span className="checkbox-track"><span className="checkbox-thumb" /></span>
                                    Extra Bait
                                </label>
                            </div>
                            <button className="btn btn-primary" type="submit">Register Angler</button>
                        </form>
                    </div>
                )}
                {modalContent === 'extraBait' && (
                    <div>
                        <h4>Add Extra Bait?</h4>
                        <p>This angler does not have extra bait. Do you want to add it and register the catch?</p>
                        <button className="btn btn-success" onClick={handleAddExtraBait}>Yes, Add Bait and Register</button>
                        <button className="btn" onClick={() => setIsModalOpen(false)}>No, Cancel</button>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default WeighIn;